/** Единая таймзона для отображения дат журнала (SSR + клиент совпадают). */
export const EDITORIAL_TIMEZONE = 'Europe/Moscow';

/**
 * Парсит строку даты из API журнала: без явного часового пояса она неоднозначна для `new Date()`
 * (на SSR и в браузере получаются разные моменты → ломается гидрация).
 * Для «наивного» ISO `YYYY-MM-DDTHH:mm:ss` считаем, что это время в Москве (UTC+3, без DST).
 */
export function parseJournalApiDate(value: string): Date {
  const s = String(value).trim();
  if (!s) {
    return new Date(NaN);
  }

  const explicitZone = /z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s);
  if (explicitZone) {
    return new Date(s);
  }

  /** Только дата — по спецификации UTC 00:00:00, одинаково в Node и в браузере */
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s);
  }

  const naiveIso = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.\d+)?$/.exec(s);
  if (naiveIso) {
    return new Date(`${naiveIso[1]}${naiveIso[2] || ''}+03:00`);
  }

  return new Date(s);
}

/**
 * Ключ календарного дня YYYY-MM-DD в Москве.
 */
export function getMoscowDateKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: EDITORIAL_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function walkBackToPreviousMoscowDay(anchor: Date, fromKey: string): string {
  let t = anchor.getTime();
  let iterations = 0;
  const maxIterations = 96;
  while (getMoscowDateKey(new Date(t)) === fromKey && iterations < maxIterations) {
    t -= 3600000;
    iterations += 1;
  }
  return getMoscowDateKey(new Date(t));
}

/**
 * «Сегодня» / «вчера» / «позавчера» по календарю Москвы относительно referenceNow.
 */
export function mapRelativeDayInMoscow(
  date: Date,
  now: Date,
): 'сегодня' | 'вчера' | 'позавчера' | undefined {
  const dKey = getMoscowDateKey(date);
  const todayKey = getMoscowDateKey(now);
  if (dKey === todayKey) {
    return 'сегодня';
  }

  const yesterdayKey = walkBackToPreviousMoscowDay(now, todayKey);
  if (dKey === yesterdayKey) {
    return 'вчера';
  }

  let t = now.getTime();
  let i = 0;
  while (getMoscowDateKey(new Date(t)) !== yesterdayKey && i < 96) {
    t -= 3600000;
    i += 1;
  }
  const beforeYesterdayKey = walkBackToPreviousMoscowDay(new Date(t), yesterdayKey);
  if (dKey === beforeYesterdayKey) {
    return 'позавчера';
  }

  return undefined;
}

export function getMoscowDateParts(d: Date): { day: number; monthIndex: number; year: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EDITORIAL_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const parts = formatter.formatToParts(d);
  const byType: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') {
      byType[p.type] = p.value;
    }
  }
  return {
    year: Number(byType.year),
    monthIndex: Number(byType.month) - 1,
    day: Number(byType.day),
  };
}

/** Время суток публикации в Москве (HH:MM, 24h). */
export function formatTimeInMoscow(d: Date): string {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: EDITORIAL_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const h = parts.find(p => p.type === 'hour')?.value ?? '00';
  const m = parts.find(p => p.type === 'minute')?.value ?? '00';
  const pad = (x: string) => (x.length < 2 ? `0${x}` : x);
  return `${pad(h)}:${pad(m)}`;
}
